import { useAuth } from "./useAuth";

// ─── Role definitions (mirrors server/acl.ts) ─────────────────────────────────

export type UserRole =
  | "admin"
  | "workspace_admin"
  | "trainer"
  | "user"
  | "guardian"
  | "editor";

// ─── Permissions catalogue ────────────────────────────────────────────────────

export interface Permissions {
  // Client management
  canListAllClients: boolean;
  canCreateClient: boolean;
  canUpdateClient: boolean;
  canDeleteClient: boolean;
  canChangeClientEmail: boolean;

  // Food database
  canReadFoods: boolean;
  canManageFoods: boolean;

  // Food categories
  canManageFoodCategories: boolean;

  // Food intake (prijem)
  canReadOwnIntake: boolean;
  canWriteIntake: boolean;

  // Body measurements
  canReadOwnMeasurements: boolean;
  canWriteMeasurements: boolean;
  canDeleteOwnMeasurements: boolean;

  // Training attendance
  canReadOwnAttendance: boolean;
  canWriteAttendance: boolean;
  canViewMonthlyAttendanceSummary: boolean;

  // Strength progress (read-only for client accounts)
  canReadOwnStrengthProgress: boolean;
  canWriteStrengthProgress: boolean;
  canManageExercises: boolean;

  // Pantry (spajza)
  canReadOwnPantry: boolean;
  canWriteOwnPantry: boolean;

  // Meal templates (sablona)
  canReadTemplates: boolean;
  canManageTemplates: boolean;
  canWriteOwnTemplates: boolean;
  canManageGlobalTemplates: boolean;

  // Recipes
  canReadRecipes: boolean;
  canWriteOwnRecipes: boolean;
  canManageGlobalRecipes: boolean;

  // Tags
  canManageTags: boolean;

  // System / admin
  canAccessSystemAdmin: boolean;
}

// ─── Role → Permission matrix ─────────────────────────────────────────────────

export const ROLE_PERMISSIONS: Record<UserRole, Permissions> = {
  admin: {
    canListAllClients: true,
    canCreateClient: true,
    canUpdateClient: true,
    canDeleteClient: true,
    canChangeClientEmail: true,

    canReadFoods: true,
    canManageFoods: true,

    canManageFoodCategories: true,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: true,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: true,
    canViewMonthlyAttendanceSummary: true,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: true,
    canManageExercises: true,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: true,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: true,

    canManageTags: true,

    canAccessSystemAdmin: true,
  },

  // workspace_admin: manages one workspace — same capabilities as admin but
  // scoped to their workspace only (enforced server-side).
  workspace_admin: {
    canListAllClients: true,
    canCreateClient: true,
    canUpdateClient: true,
    canDeleteClient: true,
    canChangeClientEmail: true,

    canReadFoods: true,
    canManageFoods: true,

    canManageFoodCategories: true,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: true,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: true,
    canViewMonthlyAttendanceSummary: true,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: true,
    canManageExercises: true,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: true,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: true,

    canManageTags: true,

    canAccessSystemAdmin: true,
  },

  trainer: {
    canListAllClients: true,
    canCreateClient: true,
    canUpdateClient: true,
    canDeleteClient: false,
    canChangeClientEmail: false,

    canReadFoods: true,
    canManageFoods: false,

    canManageFoodCategories: false,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: true,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: true,
    canViewMonthlyAttendanceSummary: true,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: true,
    canManageExercises: true,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: false,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: false,

    canManageTags: false,

    canAccessSystemAdmin: false,
  },

  user: {
    canListAllClients: false,
    canCreateClient: false,
    canUpdateClient: true,
    canDeleteClient: false,
    canChangeClientEmail: false,

    canReadFoods: true,
    canManageFoods: false,

    canManageFoodCategories: false,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: true,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: false,

    canViewMonthlyAttendanceSummary: false,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: false,
    canManageExercises: false,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: false,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: false,

    canManageTags: false,

    canAccessSystemAdmin: false,
  },

  guardian: {
    canListAllClients: false,
    canCreateClient: false,
    canUpdateClient: true,
    canDeleteClient: false,
    canChangeClientEmail: false,

    canReadFoods: true,
    canManageFoods: false,

    canManageFoodCategories: false,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: true,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: false,
    canViewMonthlyAttendanceSummary: false,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: false,
    canManageExercises: false,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: false,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: false,

    canManageTags: false,

    canAccessSystemAdmin: false,
  },

  editor: {
    canListAllClients: false,
    canCreateClient: false,
    canUpdateClient: false,
    canDeleteClient: false,
    canChangeClientEmail: false,

    canReadFoods: true,
    canManageFoods: true,

    canManageFoodCategories: true,

    canReadOwnIntake: true,
    canWriteIntake: true,

    canReadOwnMeasurements: true,
    canWriteMeasurements: false,
    canDeleteOwnMeasurements: true,

    canReadOwnAttendance: true,
    canWriteAttendance: false,
    canViewMonthlyAttendanceSummary: false,

    canReadOwnStrengthProgress: true,
    canWriteStrengthProgress: false,
    canManageExercises: false,

    canReadOwnPantry: true,
    canWriteOwnPantry: true,

    canReadTemplates: true,
    canManageTemplates: true,
    canWriteOwnTemplates: true,
    canManageGlobalTemplates: false,

    canReadRecipes: true,
    canWriteOwnRecipes: true,
    canManageGlobalRecipes: false,

    canManageTags: true,

    canAccessSystemAdmin: false,
  },
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current user's permissions based on their role.
 *
 * The permission matrix is kept in sync with `server/_core/acl.ts`.
 * When adding a new role or capability, update both files.
 *
 * @example
 * const { canWriteAttendance, canManageFoods } = usePermissions();
 * if (canWriteAttendance) { ... }
 */
export function usePermissions(): Permissions {
  const { user } = useAuth();
  const role = (user?.role as UserRole | undefined) ?? "user";
  return ROLE_PERMISSIONS[role] ?? ROLE_PERMISSIONS.user;
}
